import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMaterialStateLabel, materialStateEnum } from '../../../../constants';
import { useCreateDailyToolMutation, useDailyToolMutation } from '../../../../hooks/mutations/useDailyToolMutation';
import { useToolsQuery } from '../../../../hooks/queries/useToolsQuery';
import { DailyTool } from '../../../../services/apiSLTP/dailyReports/dailyTools';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import DropDown, { OptionType } from '../../../ui/DropDown';
import QuantityToggleGroup from '../../../ui/QuantityToggleGroup';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageValidationDailyToolModalProps extends ModalProps {
    refetch: () => void;
    dailyTool: DailyTool | null;
    activities: OptionType[];
    siteName?: string;
}

const ManageValidationDailyToolModal: React.FC<ManageValidationDailyToolModalProps> = ({
    open,
    handleClose,
    refetch,
    dailyTool,
    activities,
    siteName,
}) => {
    const { t } = useTranslation();
    const [currentDailyToolId] = useState(dailyTool?._id || '');
    const [selectedTool, setSelectedTool] = useState<OptionType | null>(
        dailyTool?.toolId
            ? {
                  label: dailyTool?.toolId.designation,
                  value: dailyTool?.toolId._id,
              }
            : null
    );
    const [selectedActivity, setSelectedActivity] = useState<OptionType | null>(
        dailyTool?.activityId
            ? {
                  label: activities.find((activity) => activity.value === dailyTool?.activityId)?.label || '',
                  value: dailyTool?.activityId || '',
              }
            : null
    );
    const [isUsed, setIsUsed] = useState<boolean>(dailyTool?.isUsed ?? true);
    const [selectedState, setSelectedState] = useState<OptionType | null>(
        dailyTool?.state
            ? {
                  label: capitalizeFirstLetter(t(getMaterialStateLabel(dailyTool?.state ?? materialStateEnum.PRESENT))),
                  value: dailyTool?.state.toString() || materialStateEnum.PRESENT.toString(),
              }
            : null
    );
    const [quantity, setQuantity] = useState<number>(dailyTool?.quantity ?? 1);

    const states = [
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.NEW))), value: materialStateEnum.NEW.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.PRESENT))), value: materialStateEnum.PRESENT.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.REMOVE))), value: materialStateEnum.REMOVE.toString() },
    ];

    const { data: dataTools } = useToolsQuery({ queries: { active: true } });

    const { mutate: createDailyTool } = useCreateDailyToolMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });
    const { mutate: updateDailyTool } = useDailyToolMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const postDailyTool = async () => {
        if (!selectedActivity || !selectedTool || !selectedState) return;
        createDailyTool({
            activityId: selectedActivity.value,
            toolId: selectedTool.value,
            state: parseInt(selectedState.value),
            isUsed,
            quantity,
        });
    };

    const patchDailyTool = async () => {
        if (currentDailyToolId)
            updateDailyTool({
                id: currentDailyToolId,
                body: {
                    activityId: selectedActivity?.value,
                    toolId: selectedTool?.value,
                    state: selectedState?.value ? parseInt(selectedState.value) : undefined,
                    isUsed,
                    quantity,
                },
            });
    };

    return (
        <ModalBody
            title={t('tool')}
            subtitle={!!currentDailyToolId.length ? t('update') : t('add')}
            description={siteName}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                !currentDailyToolId.length ? postDailyTool() : patchDailyTool();
            }}
            disableSubmit={!selectedTool?.value.length || !selectedActivity?.value.length || !selectedState?.value.length}
        >
            <DropDown
                selectedItem={selectedActivity}
                items={activities}
                handleChange={(event, value) => setSelectedActivity(value)}
                inputLabel={t('activityOf')}
                required
            />
            <DropDown
                selectedItem={selectedTool}
                items={
                    dataTools?.map((tool) => ({
                        label: tool.designation,
                        value: tool._id,
                        group: tool.toolTypeId.designation,
                    })) || []
                }
                handleChange={(event, value) => setSelectedTool(value)}
                inputLabel={t('tool')}
                grouped
                required
            />
            <DropDown
                selectedItem={selectedState}
                items={states}
                handleChange={(event, value) => setSelectedState(value)}
                inputLabel={t('state')}
                required
            />
            <QuantityToggleGroup value={quantity} onChange={(value) => setQuantity(value)} />
        </ModalBody>
    );
};

export default ManageValidationDailyToolModal;
